import React, { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";

const Terms = () => {
  const { t } = useTranslation(["terms", "translation"]);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [location]);

  return (
    <>
      <div className="min-h-full w-full">
        <div className="flex justify-center w-full h-full">
          <div className="terms-page block justify-center items-center w-full max-w-screen-lg py-20 mb-12 px-6">
            <h1 className="font-extrabold text-4xl md:text-6xl mb-8 text-center">
              {t("terms_and_policy", { ns: "translation" })}
            </h1>
            {/* Delivery and return policy */}
            <div id={t("hashes.delivery")} className="mb-8">
              <h2>{t("terms_of_delivery", { ns: "translation" })}</h2>
              <h3 className="mt-4">{t("delivery.subtitle")}</h3>
              <p>{t("delivery.delivery_info")}</p>
              <h3 className="mt-4">{t("delivery.delivery_time")}</h3>
              <p>{t("delivery.delivery_time_info")}</p>
              <h3 className="mt-4">{t("delivery.shipping_methods")}</h3>
              <p>{t("delivery.shipping_methods_info")}</p>
              <h3 className="mt-4">{t("delivery.shipping_cost")}</h3>
              <p>{t("delivery.shipping_cost_info")}</p>
              <h3 className="mt-4">{t("delivery.delivery_area")}</h3>
              <p>{t("delivery.delivery_area_info")}</p>
              <h3 className="mt-4">{t("delivery.returns")}</h3>
              <ul className="list-disc space-y-1.5 ml-8">
                <li>
                  <strong>{t("delivery.return_right")}</strong>
                  <span>{t("delivery.return_right_info")}</span>
                </li>
                <li>
                  <span>
                    <strong>{t("delivery.return_conditions")}</strong>
                    {t("delivery.return_conditions_info")}
                  </span>
                </li>
                <li>
                  <strong>{t("delivery.how_to_return")}</strong>
                  <span>
                    <Trans
                      i18nKey="delivery.how_to_return_info"
                      components={{
                        a: <a href="mailto:kundtjanst@tuutuum.se" />,
                      }}
                    >
                      {t("delivery.how_to_return_info")}
                    </Trans>
                  </span>
                </li>
                <li>
                  <strong>• {t("delivery.refund")}</strong>
                  <span>{t("delivery.refund_info")}</span>
                </li>
                <li>
                  <strong>• {t("delivery.return")}</strong>
                  <span>{t("delivery.return_info")}</span>
                </li>
              </ul>
            </div>

            <hr className="max-w-screen-xl mx-auto my-10 border-teal-300" />

            {/* Terms of Purchase */}
            <div id={t("hashes.purchase")} className="mb-8">
              <h2>{t("terms_of_purchase", { ns: "translation" })}</h2>
              <h3 className="mt-4">{t("purchase.general")}</h3>
              <p>
                <Trans
                  i18nKey="purchase.general_info"
                  components={{
                    a: <a href="https://shop.tuutuum.se" />,
                  }}
                >
                  {t("purchase.general_info")}
                </Trans>
              </p>
              <h3 className="mt-4">{t("purchase.order")}</h3>
              <p>{t("purchase.order_info")}</p>
              <h3 className="mt-4">{t("purchase.prices")}</h3>
              <p>{t("purchase.prices_info")}</p>
              <h3 className="mt-4">{t("purchase.withdrawal")}</h3>
              <p>{t("purchase.withdrawal_info")}</p>
              <h3 className="mt-4">{t("purchase.complaints")}</h3>
              <p>{t("purchase.complaints_info")}</p>
            </div>

            <hr className="max-w-screen-xl mx-auto my-10 border-teal-300" />

            {/* Privacy Policy */}
            <div id={t("hashes.privacy")} className="mb-8">
              <h2>{t("privacy_policy", { ns: "translation" })}</h2>
              <p>{t("privacy.info")}</p>
              <h3 className="mt-4">{t("privacy.collection_title")}</h3>
              <p>{t("privacy.collection")}</p>
              <ul className="list-disc space-y-1.5 mt-2 ml-8">
                <li>{t("privacy.collect_name")}</li>
                <li>{t("privacy.collect_payment_info")}</li>
                <li>{t("privacy.collect_medical_info")}</li>
                <li>{t("privacy.collect_technical_info")}</li>
              </ul>
              <h3 className="mt-4">{t("privacy.use_of_info")}</h3>
              <p>{t("privacy.usage")}</p>
              <ul className="list-disc space-y-1.5 mt-2 mb-2 ml-8">
                <li>{t("privacy.usage_orders")}</li>
                <li>{t("privacy.usage_services")}</li>
                <li>{t("privacy.usage_communication")}</li>
                <li>{t("privacy.usage_legal")}</li>
              </ul>
              <p>{t("privacy.usage_medical_info")}</p>
              <h3 className="mt-4">{t("privacy.storage_and_security")}</h3>
              <p>{t("privacy.storage_info")}</p>
              <h3 className="mt-4">{t("privacy.sharing")}</h3>
              <p>{t("privacy.sharing_info")}</p>
              <h3 className="mt-4">{t("privacy.your_rights")}</h3>
              <p>{t("privacy.rights_info")}</p>
              <ul className="list-disc space-y-1.5 ml-8 mt-2 mb-2">
                <li>{t("privacy.right_access")}</li>
                <li>{t("privacy.right_correct")}</li>
                <li>{t("privacy.right_delete")}</li>
                <li>{t("privacy.right_restrict")}</li>
              </ul>
              <p>{t("privacy.use_rights")}</p>
              {/* Cookies */}
              <h2 className="mt-10 text-3xl">{t("cookies.title")}</h2>
              <p>{t("cookies.info")}</p>

              <h3 className="mt-4">{t("cookies.third_party")}</h3>
              <p>{t("cookies.third_party_info")}</p>

              <h4 className="mt-4">{t("cookies.google_analytics")}</h4>
              <p>
                {t("cookies.google_analytics_info")}
                <Link to="https://policies.google.com/privacy">
                  https://policies.google.com/privacy
                </Link>
                .
              </p>

              <h4 className="mt-4">{t("cookies.facebook_pixel")}</h4>
              <p>
                {t("cookies.facebook_pixel_info")}
                <Link to="https://www.facebook.com/policy.php">
                  https://www.facebook.com/policy.php
                </Link>
                .
              </p>

              <hr className="max-w-screen-xl mx-auto my-10 border-teal-300" />

              {/* Contact */}
              <h2>{t("privacy.contact")}</h2>
              <p>
                <Trans
                  i18nKey="contact_info"
                  components={{
                    a1: <a href="mailto:kundtjanst@tuutuum.se" />,
                    a2: (
                      <a
                        href={`/${t("urls.contact", { ns: "translation" })}`}
                      />
                    ),
                  }}
                >
                  {t("privacy.contact_info")}
                </Trans>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
